/* eslint-disable no-undef */
/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2020 Metrological
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Router, Storage, Utils, Settings as LightningSettings } from "@lightningjs/sdk";
import Grid from "./components/grid/Grid";
import routes from "./lib/routes";
import { gridHeight, gridWidth } from "./services/grid.service";
import { ReportingService, TrackingAction } from "./services/reporting.service";
import SecretComboService from "./services/secretCombo.service";
import { Loader, ConsoleComponent } from "./widgets";

import { RemoteConfigService } from "./services/remoteConfig.service";
import { apiSetup } from "./services/api.service";
import DeviceSdk from "./lib/deviceSdk";

export default class App extends Router.App {
  constructor(props) {
    super(props);
    this.onChannelChange = this.onChannelChange.bind(this);
  }

  static getFonts() {
    return [
      { family: "Roboto-Regular", url: Utils.asset("fonts/roboto-regular.ttf") },
      { family: "Roboto-Light", url: Utils.asset("fonts/roboto-light.ttf") },
      { family: "Roboto-Italic", url: Utils.asset("fonts/roboto-italic.ttf") },
      { family: "Roboto-Bold", url: Utils.asset("fonts/roboto-bold.ttf") },
    ];
  }

  _setup() {
    RemoteConfigService.initialize()
      .then(config => {
        if (config.appConfig) {
          Router.startRouter(routes, this);
          apiSetup();
        } else {
          window.close();
        }
      })
      .catch(() => {
        window.close();
      });
  }

  _active() {
    window.addEventListener(onesdk.EVENT_TYPES.EVENT_CHANNEL_CHANGED, this.onChannelChange);
  }

  static _template() {
    return {
      ...super._template(),
      Grid: {
        type: Grid,
        alpha: 0,
      },
      Version: {
        alpha: 0,
        x: 20 * gridWidth,
        y: 20 * gridHeight,
        w: 2 * gridWidth,
        h: 1 * gridHeight,
        text: {
          text: "v" + LightningSettings.get("app", "version"),
          fontSize: 24,
          color: 0xffffffff,
          textAlign: "right",
        },
      },
      Widgets: {
        Loader: {
          type: Loader,
          zIndex: 100,
        },
        Logger: {
          type: ConsoleComponent,
          alpha: 0,
        },
      },
    };
  }

  _handleKey(keyboardPayload) {
    if (keyboardPayload.key === "Home") {
      ReportingService.trackExit();
    }

    if (SecretComboService.checkIfKeyIsSupported()) {
      SecretComboService.addKeyAndCheckForCombo(keyboardPayload.code)
        .then(comboType => {
          switch (comboType) {
            case "version":
              this.tag("Version").patch({
                alpha: !this.tag("Version").alpha,
              });
              break;

            case "grid":
              this.tag("Grid").patch({
                alpha: !this.tag("Grid").alpha,
              });
              break;

            case "logs":
              this.tag("Logger").patch({
                alpha: !this.tag("Logger").alpha,
              });
              break;

            default:
              break;
          }
        })
        .catch(error => {
          console.log("No combo");
        });
    }
    return false;
  }

  _inactive() {
    window.removeEventListener(onesdk.EVENT_TYPES.EVENT_CHANNEL_CHANGED, this.onChannelChange);
    clearInterval(this.channelChangeInterval);
    Storage.remove("sourceViewName");
  }

  async onChannelChange(payload) {
    const [changedChannel] = payload.detail;
    const channel = await DeviceSdk.getChannelById(changedChannel.currentId);
    ReportingService.silentTrack({
      action: TrackingAction.ChannelChange,
      customData: {
        destination: channel.number,
      },
      sendImmediate: true,
    });
    ReportingService.trackExit();
  }
}
